/**
 * Phase 49 Plan 49-03 — SLA task tier picker for TaskDetailSlideOver.
 *
 * Mirrors TaskStatusPicker.tsx structurally (same popover layout, same z-index,
 * same role="listbox" semantics). Click the tier pill → popover → select →
 * useUpdateTaskTier fires → optimistic update + rollback.
 *
 * Key differences vs TaskStatusPicker:
 *   - Uses useUpdateTaskTier instead of useTaskMutation (tier in-place patch).
 *   - Trigger pill is an inline span rendered via getTierColor + chipTextColor
 *     (palette-based, no tier.color field on the wire).
 *   - useClickOutside hook instead of inline mousedown useEffect (TaskStatusPicker
 *     predates the extraction; Phase 49 uses the extracted hook).
 *   - Separate useEffect for Esc (useClickOutside only covers mousedown).
 *   - Parent owns the useProjectSlaTiers fetch; tiers passed as prop.
 *   - No "Clear tier" option (Decision E — scope amendment 2026-05-27).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import type { ProjectSlaTier } from '../../services/api/projectSlaTier';
import { useUpdateTaskTier } from '../../hooks/useUpdateTaskTier';
import { getTierColor, chipTextColor } from '../../utils/format';
import { useClickOutside } from '../../hooks/useClickOutside';

export interface TaskTierPickerProps {
  taskId: number;
  projectId: number;
  audience: 'internal' | 'external';
  currentTier: { id: number; orderIndex: number; name: string };
  /** Parent passes already-fetched tiers; TaskDetailSlideOver owns the useProjectSlaTiers call. */
  tiers: ProjectSlaTier[];
  canManage: boolean;
  onError?: (message: string) => void;
}

export function TaskTierPicker({
  taskId,
  projectId,
  audience,
  currentTier,
  tiers,
  canManage,
  onError,
}: TaskTierPickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mutation = useUpdateTaskTier();

  // Sort tiers by orderIndex asc — DB insertion order is not guaranteed.
  const sortedTiers = useMemo(
    () => [...tiers].sort((a, b) => a.orderIndex - b.orderIndex),
    [tiers],
  );

  // Dismiss on outside mousedown via the extracted useClickOutside hook.
  useClickOutside(containerRef, () => setOpen(false));

  // Dismiss on Esc keydown (useClickOutside only covers mousedown).
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const handleSelect = useCallback(
    (toTier: { id: number; orderIndex: number; name: string }) => {
      if (toTier.id === currentTier.id) {
        setOpen(false);
        return;
      }
      mutation.mutate(
        {
          taskId,
          projectId: String(projectId),
          audience,
          tierId: toTier.id,
          tierData: { id: toTier.id, orderIndex: toTier.orderIndex, name: toTier.name },
        },
        {
          onError: (err) => {
            if (onError) onError(err.message ?? 'Server error');
          },
        },
      );
      setOpen(false);
    },
    [mutation, taskId, projectId, audience, currentTier.id, onError],
  );

  // Current tier pill colors (derived from locked palette, no DB color field).
  const triggerBg = getTierColor(currentTier.orderIndex);
  const triggerText = chipTextColor(triggerBg);

  // Read-only pill when the user has no rights to change tier.
  if (!canManage) {
    return (
      <span
        style={{ backgroundColor: triggerBg, color: triggerText }}
        className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold leading-none shrink-0"
        title={currentTier.name}
        data-testid="task-tier-picker"
      >
        P{currentTier.orderIndex} {currentTier.name}
      </span>
    );
  }

  const isPending = mutation.isPending;

  return (
    <div ref={containerRef} className="relative shrink-0" data-testid="task-tier-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={isPending}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={`Change tier (current: ${currentTier.name})`}
        className="inline-flex items-center gap-1 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-60 hover:ring-2 hover:ring-ms-border"
        data-testid="task-tier-picker-trigger"
      >
        <span
          style={{ backgroundColor: triggerBg, color: triggerText }}
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold leading-none"
        >
          P{currentTier.orderIndex} {currentTier.name}
        </span>
        <ChevronDownIcon className="w-3.5 h-3.5 text-ms-text-muted" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Task tier options"
          className="absolute right-0 mt-1 w-56 max-h-72 overflow-y-auto rounded-lg border border-ms-border bg-white shadow-lg z-50 py-1"
          data-testid="task-tier-picker-popover"
        >
          {sortedTiers.length === 0 ? (
            <div className="px-3 py-2 text-xs text-ms-text-muted italic">No tiers configured</div>
          ) : (
            sortedTiers.map((tier) => {
              const selected = tier.id === currentTier.id;
              const swatchBg = getTierColor(tier.orderIndex);
              const swatchText = chipTextColor(swatchBg);
              return (
                <button
                  key={tier.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => handleSelect(tier)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-ms-canvas focus:bg-ms-canvas focus:outline-none"
                  data-testid={`task-tier-option-${tier.id}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span
                      aria-hidden="true"
                      style={{ backgroundColor: swatchBg, color: swatchText }}
                      className="inline-flex items-center justify-center rounded-full px-1 py-0.5 text-[9px] font-bold shrink-0 min-w-[20px]"
                    >
                      P{tier.orderIndex}
                    </span>
                    <span className="text-sm text-ms-text truncate">{tier.name}</span>
                  </span>
                  {selected && (
                    <CheckIcon
                      className="w-4 h-4 text-ms-text-secondary shrink-0"
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
